import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEUR, fmtDate, SERVICE_TYPE_LABEL } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Users, Briefcase, FileWarning, Wallet, Calendar, TrendingUp } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="a4w-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: accent || "#DBEAFE", color: "#1E3A8A" }}>
          <Icon size={18} />
        </div>
        <p className="a4w-label">{label}</p>
      </div>
      <p className="text-3xl font-bold tracking-tight text-[#0F172A]">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p data-testid="dashboard-loading">A carregar...</p>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Visão geral da sua atividade" testid="dashboard-header" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Kpi icon={Users} label="Clientes" value={data.kpis.total_clients} />
        <Kpi icon={Briefcase} label="Serviços" value={data.kpis.total_services} accent="#E0E7FF" />
        <Kpi icon={FileWarning} label="Notas Pendentes" value={data.kpis.pending_notes} accent="#FFEDD5" />
        <Kpi icon={Wallet} label="Faturação Semana" value={fmtEUR(data.kpis.revenue_week)} accent="#DCFCE7" />
        <Kpi icon={Calendar} label="Faturação Mês" value={fmtEUR(data.kpis.revenue_month)} accent="#DCFCE7" />
        <Kpi icon={TrendingUp} label="Faturação Ano" value={fmtEUR(data.kpis.revenue_year)} accent="#DCFCE7" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="a4w-card p-6" data-testid="chart-monthly">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Faturação Mensal · 12 meses</h3>
            <Badge variant="outline" className="font-mono">EUR</Badge>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={data.chart_monthly}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtEUR(v)} contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="total" fill="#1E3A8A" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="a4w-card p-6" data-testid="chart-weekly">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Faturação Semanal · 8 semanas</h3>
            <Badge variant="outline" className="font-mono">EUR</Badge>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={data.chart_weekly}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtEUR(v)} contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={3} dot={{ fill: "#1E3A8A", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="a4w-card p-6" data-testid="expiring-list">
          <h3 className="text-lg font-semibold mb-4">A expirar em 15 dias</h3>
          {data.expiring.length === 0 ? (
            <p className="text-sm text-slate-500">Sem serviços a expirar nos próximos 15 dias.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.expiring.map(s => (
                <li key={s.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{s.client_name}</p>
                    <p className="text-xs text-slate-500">{SERVICE_TYPE_LABEL[s.type]} — {s.description}</p>
                  </div>
                  <div className="text-right">
                    <Badge style={{ background: "#FFEDD5", color: "#9A3412" }}>Expira {fmtDate(s.expires_at)}</Badge>
                    <p className="text-xs text-slate-500 mt-1">{fmtEUR(s.amount)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="a4w-card p-6" data-testid="recent-paid">
          <h3 className="text-lg font-semibold mb-4">Pagos nos últimos 15 dias</h3>
          {data.recent_paid.length === 0 ? (
            <p className="text-sm text-slate-500">Sem pagamentos recentes.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_paid.map(n => (
                <li key={n.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Nota Nº {n.number} — {n.client_snapshot?.name}</p>
                    <p className="text-xs text-slate-500">Pago em {fmtDate(n.paid_at)}</p>
                  </div>
                  <div className="text-right">
                    <Badge style={{ background: "#DCFCE7", color: "#166534" }}>Pago</Badge>
                    <p className="text-xs text-slate-500 mt-1">{fmtEUR(n.total)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8 a4w-card p-6">
        <h3 className="text-lg font-semibold mb-4">Acessos Rápidos</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/clientes" className="px-4 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium" data-testid="quick-clientes">+ Novo Cliente</Link>
          <Link to="/servicos" className="px-4 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium" data-testid="quick-servicos">+ Novo Serviço</Link>
          <Link to="/notas-encomenda" className="px-4 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium" data-testid="quick-notas">Notas de Encomenda</Link>
          <Link to="/logs" className="px-4 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium" data-testid="quick-logs">Ver Logs</Link>
        </div>
      </div>
    </div>
  );
}
